import { useMemo, useEffect, useState } from "react";

/**
 * FloatingPetals — pure CSS animated decorative VFX.
 * Renders subtle pink rose petals slowly drifting up and rotating,
 * pinned to the parent container via absolute positioning. Pointer-events
 * are disabled so it never interferes with clicks.
 *
 * Performance-aware: halves count on mobile, disables on small screens
 * when prefers-reduced-motion is set.
 *
 * Usage: drop inside any `relative` container.
 *   <div className="relative">  <FloatingPetals count={12} />  ...content
 */
export default function FloatingPetals({ count = 14, className = "" }) {
  // On mobile screens (<768px), use half the petals for better perf.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const effectiveCount = isMobile ? Math.max(4, Math.floor(count / 2)) : count;

  const petals = useMemo(
    () =>
      Array.from({ length: effectiveCount }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 10 + Math.random() * 22,
        delay: Math.random() * 12,
        duration: 14 + Math.random() * 12,
        sway: 20 + Math.random() * 60,
        rotate: Math.random() * 360,
        opacity: 0.18 + Math.random() * 0.32,
      })),
    [effectiveCount]
  );

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
    >
      {petals.map((p) => (
        <span
          key={p.id}
          className="absolute -bottom-10 petal-float"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 1.3}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            opacity: p.opacity,
            "--sway": `${p.sway}px`,
            "--rot-start": `${p.rotate}deg`,
          }}
        >
          <svg viewBox="0 0 32 42" className="w-full h-full">
            <defs>
              <radialGradient id={`petalGrad-${p.id}`} cx="50%" cy="35%" r="60%">
                <stop offset="0%" stopColor="#FCE5DF" stopOpacity="0.95" />
                <stop offset="55%" stopColor="#F0C4BC" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#C97A8B" stopOpacity="0.85" />
              </radialGradient>
            </defs>
            <path
              d="M16 1 C25 8, 30 18, 24 30 C21 36, 18 40, 16 41 C14 40, 11 36, 8 30 C2 18, 7 8, 16 1 Z"
              fill={`url(#petalGrad-${p.id})`}
            />
            <path
              d="M16 6 C16 18, 17 30, 16 38"
              stroke="#9C5868"
              strokeOpacity="0.35"
              strokeWidth="0.6"
              fill="none"
            />
          </svg>
        </span>
      ))}
      <style>{`
        @keyframes petalFloat {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot-start)); opacity: 0; }
          12%  { opacity: 1; }
          50%  { transform: translate3d(calc(var(--sway) * 0.5), -55vh, 0) rotate(calc(var(--rot-start) + 220deg)); }
          88%  { opacity: 1; }
          100% { transform: translate3d(var(--sway), -110vh, 0) rotate(calc(var(--rot-start) + 540deg)); opacity: 0; }
        }
        .petal-float {
          animation-name: petalFloat;
          animation-iteration-count: infinite;
          animation-timing-function: cubic-bezier(.45,.05,.55,.95);
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .petal-float { animation: none; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
